import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, X, ArrowUpDown, MapPin, Loader2 } from 'lucide-react';
import { api } from '../apiClient';
import EventCard from '../components/EventCard';

const CITIES = ['Mumbai', 'Delhi', 'Bangalore', 'Pune', 'Hyderabad'];

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState('');
  const [category, setCategory] = useState(searchParams.get('category') || null);
  const [city, setCity] = useState(null);
  const [sort, setSort] = useState('date');
  const [cats, setCats] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/categories').then((r) => setCats(r.data)).catch(() => {});
  }, []);

  const runSearch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/events/search', {
        params: { q: q || undefined, category: category || undefined, city: city || undefined, sort },
      });
      setResults(res.data);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [q, category, city, sort]);

  useEffect(() => { runSearch(); }, [runSearch]);

  return (
    <div className="max-w-7xl mx-auto" data-testid="search-page">
      <div className="px-6 py-4">
        <h1 className="text-2xl font-black text-gray-900 tracking-tight">Search</h1>
      </div>

      {/* Search Bar */}
      <div className="mx-6 flex items-center gap-2 bg-white border-2 border-gray-200 rounded-2xl px-4" data-testid="search-bar">
        <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <input
          data-testid="search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search events, venues..."
          onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
          className="flex-1 py-3 text-[15px] text-gray-900 placeholder-gray-400 bg-transparent focus:outline-none"
        />
        {q.length > 0 && (
          <button data-testid="search-clear-btn" onClick={() => setQ('')} className="p-1">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex gap-2 px-6 py-3 overflow-x-auto no-scrollbar">
        <button
          data-testid="filter-sort-toggle"
          onClick={() => setSort((s) => (s === 'date' ? 'popularity' : 'date'))}
          className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full bg-pink-50 border border-pink-200 text-brand-magenta text-xs font-bold"
        >
          <ArrowUpDown className="w-3 h-3" />
          Sort: {sort === 'date' ? 'Date' : 'Popularity'}
        </button>
        {[null, ...cats.map((c) => c.name)].map((c) => (
          <button
            key={c ?? 'cat-all'}
            data-testid={`filter-category-${c ?? 'all'}`}
            onClick={() => setCategory(c)}
            className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
              category === c
                ? 'bg-brand-magenta border-brand-magenta text-white'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {c ?? 'All Categories'}
          </button>
        ))}
      </div>

      <div className="flex gap-2 px-6 pb-3 overflow-x-auto no-scrollbar">
        {[null, ...CITIES].map((cv) => (
          <button
            key={cv ?? 'city-all'}
            data-testid={`filter-city-${cv ?? 'all'}`}
            onClick={() => setCity(cv)}
            className={`flex-shrink-0 flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
              city === cv
                ? 'bg-brand-magenta border-brand-magenta text-white'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            <MapPin className="w-3 h-3" />
            {cv ?? 'All Cities'}
          </button>
        ))}
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-brand-magenta" />
        </div>
      ) : results.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 px-6" data-testid="search-empty">
          <Search className="w-12 h-12 text-gray-300 mb-4" />
          <h3 className="text-lg font-extrabold text-gray-900">No events found</h3>
          <p className="text-sm text-gray-500 mt-1 text-center">Try adjusting your filters or search query.</p>
        </div>
      ) : (
        <div className="px-6 pb-8 flex flex-col gap-3">
          {results.map((item) => (
            <EventCard key={item.id} event={item} variant="wide" testId={`search-result-${item.id}`} />
          ))}
        </div>
      )}
    </div>
  );
}
